import React, { useState } from 'react';
import { isAxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { ArrowRight, Loader2, User } from 'lucide-react';
import { LogoMark } from 'components/global/LogoMark';
import { Button } from 'components/ui/button';
import { Input } from 'components/ui/input';
import { useToast } from 'components/ui/use-toast';
import { useCompleteLearnerOnboarding } from 'modules/auth';
import { useAuthContext } from 'modules/auth/context';
import { learnerReturnPath } from 'modules/auth/return-path';

/**
 * The learner questionnaire on examing.io: just a name. Learners there only take
 * the tests their group gives them, so the examing.uz exam goals, study plan and
 * plan offer do not apply. The API still needs its learner fields, so the rest
 * is sent as "not asked".
 */
export const IO_LEARNER_DEFAULTS = {
  ageRange: 'not_asked',
  targetExam: 'none',
  gradeOrLevel: 'none',
  examTimeframe: 'none',
  dailyStudyMinutes: 30,
} as const;

export const IoLearnerOnboarding: React.FC = () => {
  const { t } = useTranslation();
  const { state } = useAuthContext();
  const navigate = useNavigate();
  const location = useLocation();
  const { toast } = useToast();
  const completeLearner = useCompleteLearnerOnboarding();
  const initialName = state.user?.name && !state.user.name.includes('*') ? state.user.name : '';
  const [name, setName] = useState(initialName);
  const returnTo = learnerReturnPath(new URLSearchParams(location.search).get('returnTo')) ?? '/portal';
  const canSubmit = name.trim().length >= 2 && !completeLearner.isPending;

  const submit = async (event?: React.FormEvent) => {
    event?.preventDefault();
    if (!canSubmit) return;
    try {
      await completeLearner.mutateAsync({ name: name.trim(), ...IO_LEARNER_DEFAULTS });
      navigate(returnTo, { replace: true });
    } catch (err: unknown) {
      toast({
        title: t('learnerOb.toast_error'),
        description:
          (isAxiosError<{ message?: string }>(err) && err.response?.data?.message) || t('learnerOb.toast_save_failed'),
        variant: 'destructive',
      });
    }
  };

  return (
    <div className="flex min-h-screen flex-col bg-background text-foreground">
      <header className="flex h-16 items-center px-6">
        <LogoMark size={28} className="rounded-[8px]" />
      </header>
      <main className="flex flex-1 items-center justify-center p-4 sm:p-8">
        <form onSubmit={submit} className="w-full max-w-md space-y-6">
          <div className="text-center">
            <div className="mx-auto mb-3 flex h-12 w-12 items-center justify-center rounded-lg bg-muted text-muted-foreground">
              <User className="h-6 w-6" aria-hidden="true" />
            </div>
            <h1 className="text-2xl font-black tracking-tight sm:text-3xl">{t('learnerOb.name_title')}</h1>
            <p className="mt-2 text-sm text-muted-foreground">{t('learnerOb.name_sub')}</p>
          </div>
          <Input
            autoFocus
            type="text"
            aria-label={t('learnerOb.name_aria')}
            placeholder={t('learnerOb.name_placeholder')}
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="h-14 px-5 text-base font-semibold"
          />
          <Button type="submit" size="lg" className="h-12 w-full" disabled={!canSubmit}>
            {completeLearner.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden="true" />
            ) : (
              <>
                {t('continue')}
                <ArrowRight className="ml-2 h-4 w-4" aria-hidden="true" />
              </>
            )}
          </Button>
        </form>
      </main>
    </div>
  );
};

export default IoLearnerOnboarding;
